import { RouteConfig } from "vue-router";
import { Route } from "vue-router/types/router";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { getLoginData } from "@/lib/authHelper";
import { authoritiesRouteGuard, forbiddenRoleRouteGuard, roleRouteGuard } from "@/lib/routerGuards";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { enrichRouteMeta } from "@/router/helpers";
import { profileRoutes } from "@/router/routes/profile";
import { ROLES } from "@/const";

const routes: RouteConfig[] = [
  {
    path: "/home",
    name: "home",
    components: {
      default: () => import("@/views/PageFeed"),
      unlockFeed: () => import("@/views/UnlockFeed"),
      stories: () => import("@/views/Stories"),
    },
    children: [
      {
        path: `/home/live/:roomId`,
        name: "homeLiveByRoomId",
        props: route => ({ roomId: route.params.roomId }),
        component: () => import("@/views/StreamController"),
      },
    ],
  },
  {
    path: "/more/appointments",
    name: "appointments",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageAppointments"),
    props: (route: Route) => ({
      tab: route.query.tab || "",
    }),
    beforeEnter(_, __, next) {
      config.featureAppointments ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/storiesArchive",
    name: "storiesArchive",
    props: true,
    components: {
      default: () => import(/* webpackChunkName: "more" */ "@/views/PageStoriesArchive"),
      stories: () => import("@/views/Stories"),
    },
    beforeEnter(_, __, next) {
      config.featureStories && authoritiesRouteGuard(["getMyStoryStatistic"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/appointments/settings",
    name: "appointmentsSettings",
    props: (route: Route) => ({
      onboarding: !!route.query.onboarding,
    }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageAppointmentsSettings"),
    beforeEnter(_, __, next) {
      config.featureAppointments &&
      (authoritiesRouteGuard(["updateMyUserAvailability"]) || authoritiesRouteGuard(["updateMyAppointmentType"]))
        ? next()
        : next({ name: "/" });
    },
  },
  {
    path: "/more/appointments/settings/edit",
    name: "appointmentsSettingsEdit",
    props: (route: Route) => ({
      type: route.query.type,
    }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageAppointmentsEditSettings"),
    beforeEnter(_, __, next) {
      config.featureAppointments &&
      (authoritiesRouteGuard(["updateMyUserAvailability"]) || authoritiesRouteGuard(["updateMyAppointmentType"]))
        ? next()
        : next({ name: "/" });
    },
  },
  ...(config.featureSearchBar
    ? [
        {
          path: "/search-user",
          name: "search-user",
          props: true,
          component: () => import("@/views/PageSearchUser"),
        },
      ]
    : []),
  {
    path: "/messages",
    name: "messageThreads",
    component: () => import("@/views/PageMessageThreads"),
    props: route => ({ isMassMessage: !!route.query.massMessage }),
  },
  {
    path: "/messages/mailing",
    name: "massMessage",
    component: () => import("@/views/PageMassMessage"),
    props: route => ({ ids: route.params.ids ? route.params.ids.split(",") : undefined }),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyMassMessage"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/messages/mailing/scheduled",
    name: "scheduledMassMessages",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageScheduledMassMessage"),
    beforeEnter(_, __, next) {
      const requiredRole = ROLES.CREATOR;
      config.featureScheduledMassMessage && roleRouteGuard(requiredRole) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/messages/:userId",
    name: "messages",
    components: {
      default: () => import("@/views/PageMessages"),
      stories: () => import("@/views/Stories"),
    },
    props: {
      default: true,
    },
  },
  {
    path: "/notifications",
    name: "notifications",
    component: () => import("@/views/PageNotifications"),
    props: true,
  },
  {
    path: "/notification-settings",
    name: "notificationSettings",
    component: () => import("@/views/PageNotificationSettings"),
    props: true,
  },
  {
    path: "/more",
    name: "more",
    props: route => ({ section: route.query.section }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageMore"),
  },
  {
    path: "/more/payeeInfo",
    name: "profilePayeeInfo",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayeeInfo"),
    beforeEnter(_, __, next) {
      (config.featureSoloModel || config.featureHideBecomeCreator) && !authoritiesRouteGuard(["getMyPayoutRequest"])
        ? next({ name: "home" })
        : next();
    },
  },
  {
    path: "/more/payeeInfo/edit",
    name: "payeeInfoEdit",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayeeInfoEdit"),
    beforeEnter(_, __, next) {
      const authority =
        config.featureSoloModel || config.featureHideBecomeCreator ? "getMyPayoutRequest" : "updateMyPayeeInfo";
      authoritiesRouteGuard([authority]) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/more/sublevels",
    name: "profileSubscriptionLevels",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageSubscriptionLevels"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["updateMyContentSubscriptionLevel"]) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/more/following",
    name: "followingSettings",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageFollowingSettings"),
    beforeEnter(_, __, next) {
      config.featureWelcomeMessage && authoritiesRouteGuard(["getMyWelcomeMessage"]) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/more/cards",
    name: "profilePaymentCards",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePaymentCards"),
  },
  {
    path: "/more/balance",
    name: "balance",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageBalance"),
  },
  {
    path: "/more/massMessagesStatistics",
    name: "massMessagesStatistics",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageMassMessageStat"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const requiredAuthorities = ["updateMyContentPrice", "getMyMassMessageStat"];
      if (checkAuthorities(data?.authorities ?? [], requiredAuthorities)) next();
      else next({ name: "more" });
    },
  },
  {
    path: "/more/payout-requests",
    name: "payoutRequests",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayoutRequests"),
    beforeEnter(_, __, next) {
      config.featurePayoutRequest && authoritiesRouteGuard(["getMyPayoutRequest"]) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/more/referrals",
    name: "referrals",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageReferrals"),
    beforeEnter(_, __, next) {
      const requiredRole = [ROLES.CREATOR, ROLES.TALENT_AGENT];
      roleRouteGuard(requiredRole) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/more/creatorsByTalentRep",
    name: "creatorsByTalentRep",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageCreatorsByTalentRep"),
    beforeEnter(_, __, next) {
      const requiredRole = ROLES.TALENT_AGENT;
      roleRouteGuard(requiredRole) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/more/price-menu",
    name: "priceMenu",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageMenuPriceSettings"),
    beforeEnter(_, __, next) {
      const forbiddenRole = ROLES.ADMIN;
      forbiddenRoleRouteGuard(forbiddenRole) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/more/priceSettings",
    name: "priceSettings",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePriceSettings"),
    beforeEnter(_, __, next) {
      (config.featureCalling && authoritiesRouteGuard(["updateMyCallOptions", "updateMyCallPrice"])) ||
      (config.featurePaidSentMessage && authoritiesRouteGuard(["updateMyMessagePrice"]))
        ? next()
        : next({ name: "home" });
    },
  },
  {
    path: "/more/recurring",
    name: "manageRecurring",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageManageRecurring"),
  },
  ...(config.featureEvents
    ? [
        {
          path: "/more/events",
          name: "events",
          component: () => import(/* webpackChunkName: "more" */ "@/views/PageEvents"),
        },
      ]
    : []),
  {
    path: "/more/lists",
    name: "manageGroups",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageGroups"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyGroups"]) ? next() : next({ name: "more" });
    },
  },
  {
    path: "/more/lists/:groupId",
    name: "manageGroupUsers",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageGroupUsers"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyGroups"]) ? next() : next({ name: "more" });
    },
  },
  {
    path: "/more/vault",
    name: "manageVault",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageVault"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const requiredAuthorities = ["updateMyVault", "getMyVault", "updateMyContentPrice"];
      checkAuthorities(data?.authorities ?? [], requiredAuthorities) ? next() : next({ name: "more" });
    },
  },
  {
    path: "/more/emailNotificationsSettings",
    name: "emailNotificationsSettings",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageEmailNotificationsSettings"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (!data?.user.email) {
        next({ name: "more" });
        return;
      }
      next();
    },
  },
  ...(config.webPushNotificationsPublicKey
    ? [
        {
          path: "/more/pushNotificationsSettings",
          name: "pushNotificationsSettings",
          props: true,
          component: () => import(/* webpackChunkName: "more" */ "@/views/PagePushNotificationsSettings"),
        },
      ]
    : []),
  {
    path: "/more/account/change-password",
    name: "changePassword",
    component: () => import("@/views/PageForgotPasswordConfirmCode"),
    props: () => {
      const data = getLoginData();
      return {
        email: data?.user.email,
        changePassword: true,
      };
    },
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (!data?.user.email) {
        next({ name: "more" });
        return;
      }
      next();
    },
  },
  {
    path: "/more/account/change-password/:token",
    name: "changePasswordSet",
    component: () => import("@/views/PageForgotPasswordSet"),
    props: route => {
      return {
        token: route.params.token,
        role: route.query.role,
        changePassword: true,
      };
    },
  },
  {
    path: "/more/account/delete",
    name: "deleteAccount",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageDeleteAccount"),
    props: true,
  },
  {
    path: "/more/statements",
    name: "statements",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageStatements"),
    beforeEnter(_, __, next) {
      const requiredRoles = [ROLES.CREATOR, ROLES.TALENT_AGENT];
      config.featureStatements && roleRouteGuard(requiredRoles) ? next() : next({ name: "home" });
    },
  },
  {
    path: "/purchase-gallery/:providerId",
    name: "purchaseGallery",
    props: (route: Route) => ({
      providerId: route.params.providerId,
      itemId: route.query.id,
      type: route.query.type,
    }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePurchaseGallery"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedItems"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/purchase-gallery",
    name: "purchaseGalleryProviders",
    component: () => import("@/views/PagePurchaseGalleryProviders"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedItems"]) ? next() : next({ name: "/" });
    },
  },
  ...(config.featureFlagsDisplay
    ? [
        {
          path: "/more/features",
          name: "featuresFlags",
          component: () => import("@/views/PageFeaturesFlags"),
        },
      ]
    : []),
  {
    path: "/more/*",
    redirect: "/",
  },
  {
    path: "/profile",
    name: "myProfile",
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (data) next(getProfileUrl(data.user));
      else next("/");
    },
  },
  {
    path: "/profile/:userId/edit",
    name: "profilePageEdit",
    props: true,
    component: () => import(/* webpackChunkName: "profile" */ "@/views/PageProfileEdit"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      const requiredAuthorities = isAuthor
        ? ["updateMyQuestionaryInfo", "updateMyPersonalInfo", "updateMyFile"]
        : ["updateAnyQuestionaryInfo", "updateAnyPersonalInfo", "updateAnyFile"];
      if (checkAuthorities(data?.authorities ?? [], requiredAuthorities)) next();
      else next(getProfileUrl(to.params.userId));
    },
  },

  {
    path: "/ai-questionnaire/:userId",
    name: "aiQuestionary",
    props: (route: Route) => ({
      userId: route.params.userId,
    }),
    component: () => import(/* webpackChunkName: "profile" */ "@/views/PageAiQuestionary"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      authoritiesRouteGuard([isAuthor ? "getMyAiUserQuestionary" : "getAnyAiUserQuestionary"])
        ? next()
        : next({ name: "/" });
    },
  },
  ...(config.featureLikedContent
    ? [
        {
          path: "/liked",
          name: "likedContent",
          props: {
            default: (route: Route) => ({
              feedId: route.query.id,
              type: route.query.type,
            }),
          },
          components: {
            default: () => import("@/views/PageLikedContent"),
            unlockFeed: () => import("@/views/UnlockFeed"),
          },
        },
      ]
    : []),
  ...(config.featureFundraisingGoals
    ? [
        {
          path: "/goals",
          name: "goals",
          component: () => import(/* webpackChunkName: "more" */ "@/views/PageFundraisingGoals"),
        },
        {
          path: "/goals/:goalId",
          name: "goalsById",
          props: (route: Route) => ({ goalId: route.params.goalId }),
          component: () => import(/* webpackChunkName: "more" */ "@/views/PageFundraisingGoalDetails"),
        },
      ]
    : []),
  ...(config.featurePrivateProfile ? profileRoutes : []),
];

export default routes.map(route => enrichRouteMeta(route, { private: true }));
